import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export function Loading({ full }) {
  return (
    <div className={cn("flex items-center justify-center text-slate-500", full ? "min-h-screen" : "py-16")} data-testid="loading">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
    </div>
  );
}

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 rise">
      <div>
        {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm sm:text-base text-slate-500 mt-2 max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, hint, icon: Icon, tone = "slate", testid }) {
  const tones = { slate: "text-slate-900", green: "text-emerald-700", red: "text-rose-600", amber: "text-amber-700" };
  return (
    <div className="panel lift rise" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
      </div>
      <div className={cn("mt-3 text-2xl sm:text-3xl font-display font-extrabold", tones[tone])} data-testid={testid ? `${testid}-value` : undefined}>{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

const STATUS_STYLES = {
  valid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  expiring: "bg-amber-50 text-amber-800 border-amber-200",
  due: "bg-amber-50 text-amber-800 border-amber-200",
  partial: "bg-amber-50 text-amber-800 border-amber-200",
  notice: "bg-amber-50 text-amber-800 border-amber-200",
  scheduled: "bg-sky-50 text-sky-700 border-sky-200",
  in_progress: "bg-sky-50 text-sky-700 border-sky-200",
  skipped: "bg-slate-100 text-slate-600 border-slate-200",
  expired: "bg-rose-50 text-rose-700 border-rose-200",
  overdue: "bg-rose-50 text-rose-700 border-rose-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  suspended: "bg-rose-50 text-rose-700 border-rose-200",
  urgent: "bg-rose-50 text-rose-700 border-rose-200",
  high: "bg-orange-50 text-orange-700 border-orange-200",
};

export function StatusBadge({ status, label, testid }) {
  return (
    <span
      data-testid={testid}
      className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-semibold whitespace-nowrap", !label && "capitalize",
        STATUS_STYLES[status] || "bg-slate-50 text-slate-600 border-slate-200")}
    >
      {label || String(status || "").replace(/_/g, " ")}
    </span>
  );
}

export function EmptyState({ icon: Icon, title, text, action }) {
  return (
    <div className="panel border-dashed text-center py-14" data-testid="empty-state">
      {Icon && <Icon className="w-8 h-8 mx-auto text-slate-300 mb-3" />}
      <div className="font-display text-lg font-bold text-slate-800">{title}</div>
      {text && <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{text}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
